<template>
  <section
    v-reveal
    :id="anchorId"
    class="v2-about bg-white"
  >
    <div class="v2-about__container">
      <!-- Header -->
      <div class="v2-about__header">
        <span class="v2-about__eyebrow">About This Campaign</span>
        <h2 class="v2-about__title">{{ headline }}</h2>
      </div>

      <!-- Two-column: story card (55%) + image grid (45%) -->
      <div class="v2-about__layout">
        <!-- Story card -->
        <div class="v2-about__story-col">
          <article class="v2-about__story-card">
            <!-- Pink gradient bar -->
            <div class="v2-about__story-bar" aria-hidden="true"></div>
            <div class="v2-about__story-body">
              <div
                v-if="campaign.message"
                class="v2-about__prose"
                v-html="campaign.message"
              />
              <p v-else class="v2-about__prose-empty">Campaign story coming soon.</p>

              <!-- Buttons, separated by a hairline divider -->
              <div class="v2-about__actions">
                <a
                  :href="supportPageUrl || '#support-campaign'"
                  class="v2-about__btn v2-about__btn--primary"
                  @click="scrollToSupport"
                >
                  Support Our Mission
                </a>
                <a
                  href="#research"
                  class="v2-about__btn v2-about__btn--secondary"
                  @click="scrollToResearch"
                >
                  Learn About Research
                </a>
              </div>
            </div>
          </article>
        </div>

        <!-- Mosaic image gallery — at most 4 tiles visible at a time.
             When the gallery has more than 4 images, the 4 shown are
             chosen randomly on mount (Fisher–Yates shuffle), so
             different visits see different photos. Clicking any tile
             opens the full gallery in a CustomLightbox. The "+N more"
             overlay on the last visible tile signals additional images
             behind it. Layout adapts to count: 1 → one full tile;
             2 → side-by-side; 3 → landscape top + 2 small split; 4 → 2×2. -->
        <div class="v2-about__image-col" v-if="galleryImages.length > 0">
          <div :class="['v2-about__grid', 'v2-about__grid--' + mosaicVariant]">
            <div
              v-for="(image, i) in galleryImages"
              :key="i"
              class="v2-about__tile"
              role="button"
              tabindex="0"
              @click="onTileClick(i)"
              @keyup.enter="onTileClick(i)"
              @keyup.space.prevent="onTileClick(i)"
            >
              <img
                v-if="image.src"
                :src="image.src"
                :alt="image.alt"
                class="v2-about__tile-img"
              />
              <!-- Uploaded videos are stored with no poster
                   (CampaignImageUploader writes thumbnailUrl: ''), so the tile
                   visual is the clip's own first frame: preload="metadata" is
                   enough for the browser to paint frame 0 without pulling the
                   whole file, and muted + playsinline stop iOS from hijacking
                   the tap into fullscreen playback. -->
              <video
                v-else-if="image.videoUrl"
                :src="image.videoUrl"
                class="v2-about__tile-img"
                preload="metadata"
                muted
                playsinline
                tabindex="-1"
                aria-hidden="true"
              ></video>
              <div
                v-else
                class="v2-about__tile-empty"
                aria-hidden="true"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                  <circle cx="12" cy="13" r="3"/>
                </svg>
              </div>
              <!-- Play affordance. Without it a YouTube tile is
                   indistinguishable from a photo, which is why owners read a
                   correctly-stored video as "my video didn't show up". -->
              <div v-if="isPlayable(image)" class="v2-about__tile-play" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.2v13.6L19 12z" /></svg>
              </div>
              <div class="v2-about__tile-overlay">
                <span class="v2-about__tile-caption">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="v2-about__tile-caption-icon">
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                    <circle cx="12" cy="13" r="3"/>
                  </svg>
                  {{ image.alt }}
                </span>
              </div>
              <!-- "+N more" overlay on the last visible tile when the
                   underlying gallery has more than 4 images. -->
              <div
                v-if="i === galleryImages.length - 1 && extraCount > 0"
                class="v2-about__tile-more"
                aria-hidden="true"
              >
                +{{ extraCount }}
              </div>
            </div>
          </div>
          <CustomLightbox
            v-if="lightboxVisible"
            :media="lightboxSources"
            :index="lightboxIndex"
            :visible="true"
            @close="lightboxVisible = false"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { isUnmodifiedPrimaryClick } from '../../../../composables/useCampaignShare'
import CustomLightbox from '../../../ui/CustomLightbox.vue'
import { scrollToInPageAnchor } from '../../../../composables/useInPageScroll'
import {
  extractYoutubeID,
  youtubeEmbedUrl,
  youtubeThumbnailUrl,
} from '../../../../composables/useYouTubeUrl'

/** File extensions the media gallery accepts as a directly-playable video. */
const VIDEO_FILE = /\.(mp4|mov|m4v|webm|ogv)(\?.*)?$/i

/**
 * The YouTube id for one `slider_images` entry, from whichever field holds it.
 *
 * `videoUrl` is where the YouTube branch of CampaignsController writes the
 * canonical embed url. `url` held `watch?v={id}` on rows written before the
 * controller started storing the poster there; today it holds the poster
 * image, which the parser correctly refuses — `img.youtube.com/vi/{id}/...`
 * does not match, because the `v` alternation needs a `/` straight after it.
 *
 * Parsing lives in useYouTubeUrl so this component and the Edit Campaign form
 * accept the same link shapes; keeping a second local regex here is how the
 * two drift apart.
 *
 * Bare ids are refused: both fields are urls, so an 11-character value in
 * either is a legacy oddity rather than a video id.
 */
function youtubeIdOf(item) {
  return extractYoutubeID(item?.videoUrl, { allowBareId: false })
    || extractYoutubeID(item?.url, { allowBareId: false })
}

/**
 * Classifies one `slider_images` entry as 'image' | 'youtube' | 'video'.
 *
 * `type` is authoritative when present — it is what CampaignImageUploader and
 * the YouTube branch of CampaignsController write. The url sniffing below only
 * covers legacy rows saved before `type` was stored.
 */
function mediaKindOf(item) {
  const type = String(item?.type || '').toLowerCase()
  if (type === 'youtube') return 'youtube'
  if (type === 'video') return 'video'
  const playable = item?.videoUrl || ''
  if (youtubeIdOf(item)) return 'youtube'
  if (playable || VIDEO_FILE.test(item?.url || '')) return 'video'
  return 'image'
}

export default {
  name: 'V2About',
  components: { CustomLightbox },
  props: {
    supportPageUrl: { type: String, default: null },
    campaign: { type: Object, required: true },
    gallery: { type: Array, default: () => [] },
    anchorId: { type: String, default: 'about' },
  },
  data() {
    return {
      // Indices into `allImages` in randomized order; populated once in
      // mounted() via Fisher–Yates. Per-visit randomness — a fresh
      // shuffle on every page load means different visitors see
      // different 4 photos when the gallery has more than 4.
      shuffledOrder: [],
      lightboxVisible: false,
      lightboxIndex: 0,
    }
  },
  computed: {
    headline() {
      // Always show the campaign's own name. `hero_image_text` is a color/gradient
      // selector key (e.g. "pink"/"green"/"purple-blue"), NOT a display title, so it
      // must never be used as the heading. (Rick: the "About this Campaign" heading
      // should display the campaign's actual name.)
      return this.campaign?.name || 'About This Campaign'
    },
    /**
     * Decoded slider_images from the campaign (own first, parent as
     * fallback so supporting campaigns inherit the foundation gallery).
     * The column is stored as a JSON string and not cast on the model,
     * so we parse here. Tolerates the array-already-parsed shape too in
     * case a future cast lands.
     */
    sliderImageItems() {
      const parse = (raw) => {
        if (!raw) return []
        if (Array.isArray(raw)) return raw
        if (typeof raw !== 'string') return []
        try {
          const parsed = JSON.parse(raw)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          return []
        }
      }
      const own = parse(this.campaign?.slider_images)
      if (own.length > 0) return own
      return parse(this.campaign?.parent?.slider_images)
    },
    /** Tiles derived from the campaign's own uploaded media (own
     *  slider_images, else parent's). Empty when the campaign has none.
     *
     *  Each tile keeps `kind` and `videoUrl` alongside the poster `src`, so a
     *  video stays a video all the way through to the lightbox. Flattening
     *  every entry to `{ src, alt }` was the original bug: a YouTube item
     *  rendered as a mute still with nothing playable behind it, and an
     *  uploaded video — stored with `thumbnailUrl: ''` — fell back to
     *  `<img src="clip.mp4">`, i.e. a blank tile. */
    mediaImages() {
      return this.sliderImageItems
        .map((item, i) => {
          const kind = mediaKindOf(item)
          const noun = kind === 'image' ? 'photo' : 'video'
          // Parse once so the embed and the derived poster are built from the
          // same id. A stored `thumbnailUrl` still wins for the poster — it is
          // what the controller re-hosts — so the guarantee is about the
          // fallback pair, not about every tile.
          const youtubeId = kind === 'youtube' ? youtubeIdOf(item) : null
          // An unreadable YouTube url yields no playable url at all. Forwarding
          // the raw stored string instead is the trap this component used to
          // carry: CustomLightbox.embedOf() appends ?autoplay=1&rel=0 and frames
          // whatever it is given, so a /shorts/ link — which YouTube refuses to
          // frame — became a blank player under a play badge that promised a
          // video. `type: 'youtube'` alone is enough to land here, since it is
          // authoritative without anything having parsed the url.
          const videoUrl = kind === 'youtube'
            ? (youtubeId ? youtubeEmbedUrl(youtubeId) : '')
            : (kind === 'video' ? (item.videoUrl || item.url || '') : '')
          // An uploaded video legitimately has no poster; it keeps src '' and
          // the tile paints the clip's own first frame instead.
          const src = kind === 'image'
            ? (item.url || '')
            : (item.thumbnailUrl || (youtubeId ? youtubeThumbnailUrl(youtubeId) : ''))
          return {
            src,
            alt: `${this.campaign?.name || 'Campaign'} ${noun} ${i + 1}`,
            kind,
            videoUrl,
          }
        })
        .filter(f => f.src || f.videoUrl)
    },
    /** Images from the foundation's curated `gallery` prop
     *  (foundation_template_content.gallery). Editable by super-admins in
     *  the template editor's Gallery section. */
    curatedImages() {
      return this.gallery
        .map((g, i) => ({ src: g.src, alt: g.alt || `Photo ${i + 1}` }))
        .filter(f => f.src)
    },
    /** All non-empty images for the mosaic. Per-campaign media wins; the
     *  curated foundation gallery is the last-resort fallback. The editor's
     *  Gallery-tab preview uses this same precedence, so the preview always
     *  mirrors what's live — the campaign's current images, or the curated
     *  gallery when it has none. */
    allImages() {
      return this.mediaImages.length > 0 ? this.mediaImages : this.curatedImages
    },
    /** True when the mosaic is drawing from the curated foundation gallery
     *  rather than the campaign's own media. Used to skip the per-visit
     *  shuffle so the admin's authored order is preserved. */
    isCuratedGallery() {
      return this.mediaImages.length === 0 && this.curatedImages.length > 0
    },
    /** Up to 4 tiles rendered in the mosaic, drawn from
     *  `shuffledOrder`. Falls back to identity ordering pre-mount. */
    galleryImages() {
      return this.shuffleOrder.slice(0, 4).map(i => this.allImages[i])
    },
    /** Order used for both the mosaic and the lightbox so the two
     *  sequences line up — the user sees the same images in the same
     *  positions whether they're scanning the mosaic or pressing the
     *  lightbox arrows. Identity fallback before mounted() runs, and also
     *  whenever the stored shuffle no longer matches the current image set —
     *  editing the gallery in the template editor can swap `allImages` between
     *  own-media and curated (different lengths), so a stale permutation must
     *  not be applied against the new array. */
    shuffleOrder() {
      const fits =
        this.shuffledOrder.length > 0 &&
        this.shuffledOrder.length === this.allImages.length
      const base = fits ? this.shuffledOrder : this.allImages.map((_, i) => i)
      // Only 4 of N tiles are visible, so a random shuffle could bury a video
      // the owner just added — the "I added my YouTube video and it doesn't
      // show up" report. Videos are hoisted to the front (relative order among
      // videos, and among photos, is left exactly as shuffled). Curated
      // foundation-gallery items carry no `kind`, so this is a no-op for them.
      const isVideo = (i) => {
        const kind = this.allImages[i]?.kind
        return !!kind && kind !== 'image'
      }
      return [...base.filter(isVideo), ...base.filter((i) => !isVideo(i))]
    },
    /** Count of images hidden beyond the 4-tile cap (0 when ≤4).
     *  Drives the "+N more" overlay on the last visible tile. */
    extraCount() {
      return Math.max(0, this.allImages.length - 4)
    },
    /** Selects the CSS grid variant based on visible tile count.
     *  Capped at 4 — the --five and --six variants in scoped styles
     *  are unreachable and kept only as dead code for now. */
    mosaicVariant() {
      const n = this.galleryImages.length
      if (n <= 1) return 'one'
      if (n === 2) return 'two'
      if (n === 3) return 'three'
      return 'four'
    },
    /** Items passed to CustomLightbox. Reordered to match the mosaic's
     *  shuffled sequence — the 4 visible mosaic tiles come first (in
     *  the same positions as on the page), followed by the hidden
     *  images behind the "+N more" overlay. Polymorphic `{ src, alt }`
     *  shape unlocks the caption in the lightbox footer. */
    lightboxSources() {
      return this.shuffleOrder.map(i => ({
        src: this.allImages[i].src,
        alt: this.allImages[i].alt,
        kind: this.allImages[i].kind,
        videoUrl: this.allImages[i].videoUrl,
      }))
    },
  },
  beforeMount() {
    // A curated foundation gallery is rendered in the admin's authored order,
    // so it is NOT shuffled — leaving shuffledOrder empty lets the shuffleOrder
    // computed fall back to identity order. Per-campaign media still shuffles.
    if (this.isCuratedGallery) return
    // Fisher–Yates shuffle of [0..n-1] — populates shuffledOrder once
    // per component instance. Runs in beforeMount() (not mounted()) so
    // the first paint already uses the shuffled order; otherwise tiles
    // would briefly flash in identity order before re-rendering.
    const n = this.allImages.length
    const order = Array.from({ length: n }, (_, i) => i)
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[order[i], order[j]] = [order[j], order[i]]
    }
    this.shuffledOrder = order
  },
  methods: {
    /** True for a tile that opens playable media in the lightbox. */
    isPlayable(item) {
      return !!item?.videoUrl
    },
    scrollToSupport(e) {
      if (this.supportPageUrl) {
        if (!isUnmodifiedPrimaryClick(e)) return
        // Support Hub rollout: open the takeover in place. The href stays
        // on the anchor so the shareable link is still copyable.
        e?.preventDefault?.()
        this.emitter.emit('open-support-hub', { returnFocus: e?.currentTarget })
        return
      }
      // Smooth-scroll to the lower-page "Support This Campaign" section
      // (V2SupportOnboarding, id="support-campaign") rather than opening the
      // donation modal — the visitor sees the fundraise / share / donate
      // options together and picks what fits. The shared helper owns the
      // sticky-header offset so this anchor lands at the same y-position as
      // every other in-page anchor; the real href="#support-campaign" is the
      // no-JS / older-template fallback when the target is missing.
      scrollToInPageAnchor('support-campaign', e)
    },
    scrollToResearch(e) {
      // Smooth-scroll to the #research section (V2PrecisionAnalysis) with the
      // shared sticky-header offset so it lands cleanly below the bar instead
      // of the bare native jump landing under the header. If the section isn't
      // on the page the helper is a no-op and the native href="#research" jump
      // takes over — same delegation to useInPageScroll as the other in-page anchors.
      scrollToInPageAnchor('research', e)
    },
    /**
     * Tile click handler. `i` is the index of the clicked tile within
     * `galleryImages` (0–3) — and because `lightboxSources` shares the
     * same `shuffleOrder` as `galleryImages`, that index maps 1:1 to
     * the lightbox position, so the lightbox starts on exactly the
     * photo the user clicked. When the user clicks the last visible
     * tile AND there are images hidden behind a "+N more" overlay,
     * jump to index 4 instead — the first hidden image, per the UX
     * convention that "+N more" takes the user to what they haven't seen.
     */
    onTileClick(i) {
      const isLastVisible = i === this.galleryImages.length - 1
      this.lightboxIndex = (isLastVisible && this.extraCount > 0) ? 4 : i
      this.lightboxVisible = true
    },
  },
}
</script>

<style scoped>
.v2-about {
  /* py-16 lg:py-24 */
  padding-top: 64px;
  padding-bottom: 64px;
}
@media (min-width: 1024px) {
  .v2-about { padding-top: 96px; padding-bottom: 96px; }
}

.v2-about__container {
  max-width: 1400px;
  margin-inline: auto;
  padding-inline: 24px;
}
@media (min-width: 768px)  { .v2-about__container { padding-inline: 48px; } }
@media (min-width: 1024px) { .v2-about__container { padding-inline: 64px; } }

/* ─── Header ─────────────────────────────────────────────── */
.v2-about__header {
  text-align: center;
  margin-bottom: 40px;
}
.v2-about__eyebrow {
  display: inline-block;
  color: var(--sideout-pink);
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  line-height: 24px;
}
.v2-about__title {
  margin-top: 12px;
  color: var(--sideout-navy);
  font-family: 'Red Hat Display', sans-serif;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 800;
  text-wrap: balance;
}
@media (min-width: 768px) {
  .v2-about__title { font-size: 36px; }
}

/* ─── Two-column layout (story 55 / images 45) ──────────── */
.v2-about__layout {
  display: flex;
  flex-direction: column;
  gap: 32px;
}
@media (min-width: 1024px) {
  .v2-about__layout {
    flex-direction: row;
    /* stretch — the image column matches the story card's height per
       client mockup. To make this work without distorting tiles, the
       desktop overrides below (a) drop tile `aspect-ratio`, (b) set
       grid rows to `1fr` so they share the column's height, and
       (c) rely on `object-fit: cover` on the image to absorb the crop.
       Long stories crop tiles aggressively — accepted trade. */
    align-items: stretch;
    gap: 40px;
  }
  .v2-about__story-col { flex: 1 1 0%; min-width: 0; }
  .v2-about__image-col {
    width: 45%;
    flex-shrink: 0;
    /* Column flex so the inner grid can `flex: 1` to fill the column's
       full height. The column itself gets its height from the parent
       row's `align-items: stretch` — `height: 100%` would resolve to 0
       because the row's height is content-derived. */
    display: flex;
    flex-direction: column;
  }
}

/* ─── Story card ──────────────────────────────────────────── */
.v2-about__story-card {
  background: #ffffff;
  border: 1px solid var(--sideout-border);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.v2-about__story-bar {
  height: 6px;
  background: linear-gradient(
    90deg,
    var(--sideout-pink) 0%,
    rgba(218, 24, 132, 0.6) 55%,
    transparent 100%
  );
}
.v2-about__story-body {
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
@media (min-width: 768px)  { .v2-about__story-body { padding: 32px; } }
@media (min-width: 1024px) { .v2-about__story-body { padding: 40px; } }

/* Prose — mirrors Lovable's prose-xl custom overrides */
.v2-about__prose {
  flex: 1;
  font-family: 'Red Hat Display', sans-serif;
}
.v2-about__prose :deep(p) {
  color: var(--sideout-muted);
  font-size: 18px;
  line-height: 1.65;
  margin: 0;
}
/* CKEditor-style <p><br></p> spacer paragraphs — collapse to zero so
   the rhythm is a clean 24px between real paragraphs, not 24px + the
   height of an empty line + another 24px (which reads as too much
   whitespace on longer campaign stories). */
.v2-about__prose :deep(p:has(> br:only-child)),
.v2-about__prose :deep(p:empty) {
  margin: 0;
  height: 0;
  overflow: hidden;
}
.v2-about__prose :deep(p + p) {
  margin-top: 24px;
}
.v2-about__prose :deep(strong) {
  color: var(--sideout-navy);
  font-weight: 800;
}
.v2-about__prose :deep(a) {
  color: var(--sideout-pink);
  text-decoration: none;
}
.v2-about__prose :deep(a:hover) {
  text-decoration: underline;
}
.v2-about__prose :deep(ul),
.v2-about__prose :deep(ol) {
  color: var(--sideout-muted);
  padding-left: 1.5rem;
  margin: 1rem 0;
}
.v2-about__prose :deep(li) {
  margin: 0.25rem 0;
}
.v2-about__prose :deep(li::marker) {
  color: var(--sideout-pink);
}
.v2-about__prose :deep(blockquote) {
  border-left: 4px solid var(--sideout-pink);
  background: rgba(247, 248, 251, 0.6);
  border-radius: 0 0.5rem 0.5rem 0;
  padding: 0.5rem 1rem;
  margin: 1.25rem 0;
}
.v2-about__prose :deep(h1),
.v2-about__prose :deep(h2),
.v2-about__prose :deep(h3),
.v2-about__prose :deep(h4) {
  color: var(--sideout-navy);
  font-weight: 800;
}
.v2-about__prose :deep(img) {
  border-radius: 12px;
}
.v2-about__prose-empty {
  color: var(--sideout-muted);
  font-style: italic;
}

/* Divider + buttons inside the card */
.v2-about__actions {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--sideout-border);
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.v2-about__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 12px 28px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, opacity 0.15s, border-color 0.15s, transform 0.15s;
  border: 2px solid transparent;
  line-height: 1;
  font-family: 'Red Hat Display', sans-serif;
  text-decoration: none; /* both CTAs are <a> now — keep them button-flat */
}
.v2-about__btn--primary {
  background-color: var(--sideout-pink);
  color: #ffffff;
}
.v2-about__btn--primary:hover { opacity: 0.9; }
.v2-about__btn--primary:active { transform: scale(0.98); }
.v2-about__btn--secondary {
  background-color: transparent;
  color: var(--sideout-navy);
  border-color: var(--sideout-navy);
  padding: 10px 26px;
}
.v2-about__btn--secondary:hover {
  background-color: var(--sideout-navy);
  color: #ffffff;
}
.v2-about__btn--secondary:active { transform: scale(0.98); }

/* ─── Image grid (count-aware mosaic) ────────────────────── */
.v2-about__grid {
  display: grid;
  gap: 12px;
  width: 100%;
  /* Mobile: height flows from tile aspect-ratios (preserved below).
     Desktop: height is forced to 100% so the column stretches to the
     story card's height (see the @media block lower in this file). */
}

/* 1 image → one big tile. */
.v2-about__grid--one {
  grid-template-columns: 1fr;
  grid-template-rows: auto;
}
/* 2 images → side-by-side. */
.v2-about__grid--two {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto;
}
/* 3 images → wide landscape on top, 2 small below (Rick's brief:
   "2 small and one landscape"). Row heights flow from the tiles'
   own aspect-ratios. */
.v2-about__grid--three {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  grid-template-areas:
    'a a'
    'b c';
}
.v2-about__grid--three .v2-about__tile:nth-child(1) { grid-area: a; }
.v2-about__grid--three .v2-about__tile:nth-child(2) { grid-area: b; }
.v2-about__grid--three .v2-about__tile:nth-child(3) { grid-area: c; }

/* 4 images → the original 2×2. */
.v2-about__grid--four {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
}

/* 5 images → 3 cols × 2 rows with the first tile spanning 2 cols
   in row 1. Creates a bento feel without a gap. */
.v2-about__grid--five {
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto;
  grid-template-areas:
    'a a b'
    'c d e';
}
.v2-about__grid--five .v2-about__tile:nth-child(1) { grid-area: a; }
.v2-about__grid--five .v2-about__tile:nth-child(2) { grid-area: b; }
.v2-about__grid--five .v2-about__tile:nth-child(3) { grid-area: c; }
.v2-about__grid--five .v2-about__tile:nth-child(4) { grid-area: d; }
.v2-about__grid--five .v2-about__tile:nth-child(5) { grid-area: e; }

/* 6 images → uniform 3×2 bento. */
.v2-about__grid--six {
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto;
}

/* Below the desktop breakpoint, 3-column variants would crush tiles
   too small — collapse back to 2 columns and let rows flow. */
@media (max-width: 1023px) {
  .v2-about__grid--five,
  .v2-about__grid--six {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    grid-template-areas: none;
  }
  .v2-about__grid--five .v2-about__tile,
  .v2-about__grid--six .v2-about__tile {
    grid-area: auto;
  }
}

.v2-about__tile {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  /* Mobile sizing — 4:3 photo feel by default; --one and the wide top
     tile in --three switch to 16:9 below to suit hero / video-thumbnail
     content. On desktop these aspect-ratios are overridden to `auto`
     (see the desktop @media block below) so tile height comes from the
     grid row's `1fr` track instead. */
  aspect-ratio: 4 / 3;
  background: var(--sideout-surface);
  cursor: pointer;
}
.v2-about__grid--one .v2-about__tile,
.v2-about__grid--three .v2-about__tile:nth-child(1) {
  aspect-ratio: 16 / 9;
}

/* Desktop: image column stretches to match the story card height, so
   tiles can no longer rely on their intrinsic aspect-ratio for sizing.
   Grid rows become `1fr` (sharing column height) and tile aspect-ratio
   is dropped — `object-fit: cover` on the image absorbs the crop. This
   block must come AFTER the base tile rules above so the cascade's
   source-order tiebreak favors `aspect-ratio: auto`. */
@media (min-width: 1024px) {
  .v2-about__grid {
    /* `flex: 1` (not `height: 100%`) so the grid grows to fill the
       column's vertical space — works because the column is now
       `flex-direction: column`. `1fr` rows below distribute this
       grown height between the tiles. */
    flex: 1;
  }
  .v2-about__grid--one  { grid-template-rows: 1fr; }
  .v2-about__grid--two  { grid-template-rows: 1fr; }
  .v2-about__grid--three { grid-template-rows: 1.3fr 1fr; } /* hero on top */
  .v2-about__grid--four { grid-template-rows: 1fr 1fr; }
  .v2-about__tile,
  .v2-about__grid--one .v2-about__tile,
  .v2-about__grid--three .v2-about__tile:nth-child(1) {
    aspect-ratio: auto;
  }
}
.v2-about__tile-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 500ms cubic-bezier(0.4, 0, 0.2, 1);
}
.v2-about__tile:hover .v2-about__tile-img {
  transform: scale(1.05);
}
.v2-about__tile-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--sideout-border);
}
.v2-about__tile-empty svg {
  width: 40px;
  height: 40px;
}
.v2-about__tile-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 12px;
  background-color: transparent;
  transition: background-color 300ms ease;
}
.v2-about__tile:hover .v2-about__tile-overlay {
  background-color: rgba(29, 37, 73, 0.3); /* navy/30 */
}
.v2-about__tile-caption {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 300ms ease;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}
.v2-about__tile:hover .v2-about__tile-caption {
  opacity: 1;
}
.v2-about__tile-caption-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

/* "+N more" overlay on the last visible tile when the underlying
   gallery exceeds the 4-tile cap. Sits above the hover overlay
   (z layer) and uses `pointer-events: none` so clicks pass through
   to the parent tile — the tile's own click handler routes "+N more"
   clicks to the first hidden image in the lightbox. */
.v2-about__tile-more {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(29, 37, 73, 0.72);
  color: #ffffff;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 0.02em;
  pointer-events: none;
  z-index: 2;
}

/* Play badge on a video tile. Solid disc + solid glyph, no gradient or
   shadow, per the V2 button/panel convention. z-index 2 puts it above the
   caption overlay; the "+N more" scrim is later in the DOM at the same level
   so it still wins on the one tile that carries both. */
.v2-about__tile-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: #ffffff;
  color: var(--sideout-pink);
  pointer-events: none;
  z-index: 2;
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
}
.v2-about__tile-play svg {
  width: 26px;
  height: 26px;
  /* Optical centering — a triangle's visual mass sits left of its box. */
  margin-left: 3px;
}
.v2-about__tile:hover .v2-about__tile-play {
  transform: translate(-50%, -50%) scale(1.08);
}
@media (max-width: 767px) {
  .v2-about__tile-play {
    width: 44px;
    height: 44px;
  }
  .v2-about__tile-play svg {
    width: 20px;
    height: 20px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .v2-about__tile-img,
  .v2-about__tile-overlay,
  .v2-about__tile-caption,
  .v2-about__tile-play,
  .v2-about__btn {
    transition-duration: 60ms;
  }
  .v2-about__tile:hover .v2-about__tile-img {
    transform: none;
  }
  .v2-about__tile:hover .v2-about__tile-play {
    transform: translate(-50%, -50%);
  }
}
</style>
